import type { DefaultDateTimeFormatSchema, DefaultNumberFormatSchema } from 'vue-i18n'

export const messages = {

  /**
   * Metadata about this language/locale
   */
  meta: {
    name: 'English',
  },

  /**
   * Labels for buttons and other UI elements which initiate actions.
   */
  actions: {
    add_spot_rate: 'Add Spot Rate',
    add_tier: 'Add Tier',
    all_days: 'All', // as in, "enable all weekdays in this weekday picker"
    apply: 'Apply',
    bulk_edit: 'Bulk Edit',
    bulk_edit_cost: 'Bulk Edit Cost',
    bulk_edit_inventory: 'Bulk Edit Inventory',
    cancel: 'Cancel',
    clear_all_discounts: 'Clear All Discounts',
    clear_selection: 'Clear Selection',
    close: 'Close',
    confirm_account: 'Confirm Account',
    create_blackout: 'Create Blackout',
    create_fee: 'Create Fee',
    create_location: 'Create Location',
    create_location_product: 'Create Product',
    create_organization: 'Create Organization',
    create_parcs: 'Create PARCS',
    create_product: 'Create Product',
    create_user: 'Create User',
    delete: 'Delete',
    delete_spot_rate: 'Delete Spot Rate',
    delete_tier: 'Delete Tier',
    edit: 'Edit',
    edit_default_rates: 'Edit Default Rates',
    edit_selected: 'Edit Selected',
    edit_spot_rate: 'Edit Spot Rate',
    go_back_to_login: 'Go back to login',
    go_home: 'Go Home',
    log_out: 'Log Out',
    manage_spot_rates: 'Manage Spot Rates',
    midnight: 'Midnight', // as in, "set this time picker to midnight"
    proceed: 'Proceed',
    reload: 'Reload',
    remove: 'Remove',
    reset: 'Reset',
    reset_password: 'Reset Password',
    save: 'Save',
    set_all_to_default: 'Set All to Default',
    sign_in: 'Sign In',
    submit: 'Submit',
    sync_rates: 'Sync Rates',
    update_inventory: 'Update Inventory',
    update_inventory_abbr: 'Update Inv',
    view: 'View',
    view_breakdown: 'View Breakdown',
    view_history: 'View History',
    weekdays: 'Weekdays', // as in, "enable M-F in this weekday picker"
    weekends: 'Weekends', // as in, "enable only Saturday & Sunday in this weekday picker"
  },

  /**
   * Messages which confirm the success of an action, warn the user about a
   * failure, or prompt the user to take some action. Typically displayed in a
   * toast notification, or in an alert bar.
   */
  alerts: {
    blackout_created: 'Blackout created',
    blackout_deleted: 'Blackout deleted',
    blackout_updated: 'Blackout updated',
    confirm_delete_blackout: 'Are you sure you want to delete the blackout "{blackout_name}"? This cannot be undone!',
    confirm_delete_fee: 'Are you sure you want to delete this fee? This cannot be undone!',
    confirm_delete_parcs: 'Are you sure you want to delete this PARCS? This cannot be undone!',
    confirm_delete_product: 'Are you sure you want to delete the product "{product_name}"? This cannot be undone!',
    couldnt_create_blackout: 'Couldn\'t create blackout!',
    couldnt_create_fee: 'Couldn\'t create fee!',
    couldnt_create_organization: 'Couldn\'t create organization!',
    couldnt_create_parcs: 'Couldn\'t create PARCS!',
    couldnt_delete_blackout: 'Couldn\'t delete blackout!',
    couldnt_delete_fee: 'Couldn\'t delete fee!',
    couldnt_delete_parcs: 'Couldn\'t delete PARCS!',
    couldnt_delete_product: 'Couldn\'t delete product!',
    couldnt_load_channels: 'Couldn\'t load channels!',
    couldnt_load_data: 'Couldn\'t load data!',
    couldnt_load_facilities: 'Couldn\'t load facilities!',
    couldnt_load_inventory: 'Couldn\'t load inventory!',
    couldnt_load_locations: 'Couldn\'t load locations!',
    couldnt_load_organizations: 'Couldn\'t load organizations!',
    couldnt_load_parcs: 'Couldn\'t load PARCS!',
    couldnt_load_products: 'Couldn\'t load products!',
    couldnt_load_transactions: 'Couldn\'t load transactions!',
    couldnt_load_users: 'Couldn\'t load users!',
    couldnt_load_user_solutions: 'Couldn\'t load user solutions!',
    couldnt_save_location: 'Couldn\'t save location!',
    couldnt_save_product: 'Couldn\'t save product!',
    couldnt_update_organization: 'Couldn\'t update organization!',
    couldnt_update_blackout: 'Couldn\'t update blackout!',
    couldnt_update_fee: 'Couldn\'t update fee!',
    couldnt_update_parcs: 'Couldn\'t update PARCS!',
    couldnt_update_profile: 'Couldn\'t update your profile!',
    couldnt_update_solutions: 'Couldn\'t update solutions!',
    couldnt_update_spot_rates: 'Couldn\'t update spot rates!',
    couldnt_update_user_solutions: 'Couldn\'t update user solutions!',
    couldnt_update_venues: 'Couldn\'t update venue! | Couldn\'t update venue! | Couldn\'t update venues!',
    csv_download_failed: 'CSV download failed!',
    defaults_for_event_products_can_only_be_set_at_creation_time: 'Defaults for Event products can only be set at creation time',
    fee_created: 'Fee created',
    fee_deleted: 'Fee deleted',
    fee_updated: 'Fee updated',
    invalid_login_credentials: 'Incorrect credentials. Please double-check your username and password.',
    location_created: 'Location created',
    location_blackouts_not_possible: 'No channels that support blackouts are associated with the selected location(s)',
    location_updated: 'Location updated',
    no_location_selected: 'No location selected',
    no_location_venues_found: 'No venues found for this location. If you just added an event product, you may need to wait a few minutes before venues will show up here.',
    no_matching_products: 'No matching products',
    nothing_was_changed: 'Nothing was changed',
    organization_created: 'Organization created',
    organization_updated: 'Organization updated',
    page_not_found_explanation: 'Sorry, but the URL you requested does not exist.',
    parcs_created: 'PARCS created',
    parcs_deleted: 'PARCS deleted',
    parcs_products_do_not_have_default_rates: 'PARCS products do not have Default Rates.',
    parcs_updated: 'PARCS updated',
    please_select_a_product: 'Please select a product',
    product_created: 'Product created',
    product_deleted: 'Product deleted',
    product_saved: 'Product saved',
    product_updated: 'Product updated',
    profile_updated: 'Profile updated',
    profile_form_instructions: 'This information will only be shared with Ocra, but please be careful what you share.',
    reset_password_instructions: 'A verification code has just been sent to your email address. To reset your password, please enter that code below.',
    sign_in_to_your_account: 'Sign in to your account',
    solutions_updated: 'Solutions updated',
    spot_rate_updated: 'Spot rate updated',
    spothero_iq_products_do_not_have_default_rates: 'SpotHero IQ products do not have Default Rates.',
    sync_rates_success: 'Rates are now syncing to channels',
    user_created: 'User created',
    user_solutions_updated: 'User solutions updated',
    user_updated: 'User updated',
    venues_updated: 'Venue updated | Venue updated | Venues updated',
  },

  enums: {
    blackout_meta_statuses: {
      current: 'Current',
      expired: 'Expired',
      deleted: 'Deleted',
    },
    location_statuses: {
      onboarded: 'Onboarded',
      live: 'Live',
      archived: 'Archived',
    },
    product_types: {
      garage_ground_floor: 'Garage Ground Floor',
      self_covered: 'Self - Covered',
      self_covered_oversized: 'Self - Covered (Oversized)',
      self_curbside: 'Self - Curbside',
      self_curbside_oversized: 'Self - Curbside (Oversized)',
      self_indoor: 'Self - Indoor',
      self_indoor_oversized: 'Self - Indoor (Oversized)',
      self_rooftop: 'Self - Rooftop',
      self_rooftop_oversized: 'Self - Rooftop (Oversized)',
      self_uncovered: 'Self - Uncovered',
      self_uncovered_oversized: 'Self - Uncovered (Oversized)',
      valet_covered: 'Valet - Covered',
      valet_covered_oversized: 'Valet - Covered (Oversized)',
      valet_curbside: 'Valet - Curbside',
      valet_curbside_oversized: 'Valet - Curbside (Oversized)',
      valet_indoor: 'Valet - Indoor',
      valet_indoor_oversized: 'Valet - Indoor (Oversized)',
      valet_rooftop: 'Valet - Rooftop',
      valet_rooftop_oversized: 'Valet - Rooftop (Oversized)',
      valet_uncovered: 'Valet - Uncovered',
      valet_uncovered_oversized: 'Valet - Uncovered (Oversized)',
    },
    read_write_permissions: {
      none: 'None',
      read: 'Read',
      write: 'Write',
    },
    repeat_customer_categories: {
      one_time: 'One-Time',
      repeat: 'Repeat',
    },
    solutions: {
      universal_inventory: 'Universal Inventory',
      rate_management: 'Rate Management',
      blackouts: 'Blackouts',
      reporting: 'Reporting',
      // these aliases are because the Solutions enum uses camelCase instead of snake_case. we should fix that.
      universalInventory: 'Universal Inventory',
      rateManagement: 'Rate Management',
      terminal: 'Terminal',
      uri: 'URI',
    },
    tax_fee_commission_frequency_types: {
      daily: 'Daily',
      'one-time': 'One-Time',
    },
    tax_fee_commission_target_types: {
      on_top_of_reservation_revenue: 'On Top of Reservation Revenue',
      reservation_revenue: 'Reservation Revenue',
      add_on: 'Add-On',
      add_on_commission: 'Add-On (Commission)',
    },
    user_roles: {
      super_admin: 'Super Admin',
      admin: 'Admin',
      user: 'User',
    },
    verticals: {
      airport: 'Airport',
      event: 'Event',
      monthly: 'Monthly',
      on_demand: 'On-Demand',
      parcs: 'PARCS',
      spothero_in_out: 'SpotHero In & Out',
      spothero_iq: 'SpotHero IQ',
      transient: 'Transient',
    },
    weekdays: {
      sun: 'Sunday',
      mon: 'Monday',
      tue: 'Tuesday',
      wed: 'Wednesday',
      thu: 'Thursday',
      fri: 'Friday',
      sat: 'Saturday',
    },
    weekdays_abbr: {
      sun: 'Sun',
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
    },
    weekdays_initial: {
      sun: 'S',
      mon: 'M',
      tue: 'T',
      wed: 'W',
      thu: 'T',
      fri: 'F',
      sat: 'S',
    },
    weekdays_plural: {
      sun: 'Sundays',
      mon: 'Mondays',
      tue: 'Tuesdays',
      wed: 'Wednesdays',
      thu: 'Thursdays',
      fri: 'Fridays',
      sat: 'Saturdays',
    },
  },

  misc: {
    booking_page_domain_hint: 'e.g., booking.yourdomain.com.',
    booking_page_locations_hint: 'The locations that will be bookable via this page.',
    duration_hours: '{hours} hr',
    duration_hours_minutes: '{hours} hr {minutes} min',
    flat: 'Flat',
    forgot_your_password: 'Forgot your password?',
    go_back_to_login: 'Go back to login',
    live: 'Live',
    no: 'No',
    none: 'None',
    occupancy_heatmap_summary: 'You get the most rentals on {weekday_plural} from {window}, with an average of {rentals} rental(s).',
    onboarded: 'Onboarded',
    password_requirements: 'At least 8 characters, with at least one number and one uppercase letter',
    permissions_none: 'None',
    permissions_read: 'Read',
    permissions_write: 'Write',
    platform_access: 'Platform Access',
    sync_rates_explanation: 'Updates to default rates are not immediately dispatched to the channels. Submitting this form will sync any pending updates to the channels.',
    terminal_access: 'Terminal Access',
    tiered: 'Tiered',
    unchanged: 'Unchanged',
    unknown_item: '(unknown)',
    use_product_settings: 'Use product settings',
    username_requirements: 'Lowercase letters, digits, and underscores only',
    yes: 'Yes',
  },

  /**
   * Titles/labels for nav items, pages, cards, modals, table headers, form
   * controls, etc. Should use title case.
   */
  titles: {
    action: 'Action',
    actions: 'Actions...', // for ActionGroup menu button
    activity: 'Activity',
    add_product: 'Add Product',
    add_tier: 'Add Tier',
    address: 'Address',
    advanced_booking_window: 'Advanced Booking Window',
    after_event_colon: 'After Event:',
    all: 'All',
    amount: 'Amount',
    apply: 'Apply', // as in "apply the values that the user has entered into this form"
    are_you_sure: 'Are you sure?',
    arrivals: 'Arrivals',
    average_daily_transactions: 'Average Daily Transactions',
    average_daily_rate: 'Average Daily Rate',
    average_occupancy: 'Average Occupancy',
    average_rate: 'Average Rate',
    average_rate_by_channel: 'Average Rate by Channel',
    average_rate_by_vertical: 'Average Rate by Vertical',
    before_event_colon: 'Before Event:',
    blackout_history: 'Blackout {id} History',
    blackouts: 'Blackouts',
    booking_duration: 'Booking Duration',
    booking_pages: 'Booking Pages',
    breakdowns: 'Breakdowns',
    calendar: 'Calendar',
    cancel: 'Cancel',
    cancellation_rate: 'Cancellation Rate',
    channel: 'Channel',
    channel_breakdown: 'Channel Breakdown',
    channel_facility: 'Channel Facility',
    channel_id: 'Channel ID',
    channel_override: 'Channel Override',
    channel_side_facility_id: 'Channel-Side Facility ID',
    channels: 'Channels',
    channels_one_or_more: 'Channel(s)',
    close: 'Close', // in the sense of "closing time"
    closed: 'Closed', // in the sense of "not open to customers"
    columns: 'Columns',
    commission: 'Commission',
    company: 'Company',
    confirm_your_account: 'Confirm your account',
    contact_email: 'Contact Email',
    contact_name: 'Contact Name',
    contact_phone: 'Contact Phone',
    cost: 'Cost',
    costs: 'Costs',
    cost_structure: 'Cost Structure',
    count: 'Count',
    count_over_24: 'Count/24',
    create_blackout: 'Create Blackout',
    create_fee: 'Create Fee',
    create_organization: 'Create Organization',
    create_parcs: 'Create PARCS',
    create_user: 'Create User',
    created: 'Created',
    custom: 'Custom',
    customers: 'Customers',
    daily_revenue: 'Daily Revenue',
    dark_mode: 'Dark Mode',
    date: 'Date',
    dates: 'Dates',
    date_range: 'Date Range',
    date_time: 'Date/Time',
    date_time_utc: 'Date/Time (UTC)',
    dates_one_or_more: 'Date(s)',
    day: 'Day', // as in "day of the week" - Sunday, Monday, etc.
    default_rates_abbr: 'Default', // used as shorthand for "Default Rates" in tight spaces
    default_cost: 'Default Cost',
    default_inventory: 'Default Inventory',
    default_inventory_colon: 'Default Inventory:',
    default_price_colon: 'Default Price:',
    default_rate: 'Default Rate',
    default_rates: 'Default Rates',
    delete_blackout: 'Delete Blackout',
    delete_fee: 'Delete Fee',
    delete_parcs: 'Delete PARCS',
    delete_product: 'Delete Product',
    deleted: 'Deleted',
    details: 'Details',
    departures: 'Departures',
    discounts: 'Discounts',
    discounts_spothero: 'Discounts – SpotHero',
    do_not_send_rates_to_channel: 'Do Not Send Rates to Channel',
    domain: 'Domain',
    download_csv: 'Download CSV',
    download_transactions_report: 'Download Transactions Report',
    duration: 'Duration',
    early_bird: 'Early Bird',
    early_bird_rate: 'Early Bird Rate',
    early_booking: 'Early Booking',
    early_booking_discounts: 'Early Booking Discounts',
    edit: 'Edit',
    edit_blackout: 'Edit Blackout',
    edit_inventory: 'Edit Inventory',
    edit_organization: 'Edit Organization',
    edit_parcs: 'Edit PARCS',
    edit_product: 'Edit Product',
    edit_spot_rate: 'Edit Spot Rate',
    edit_user_with_username: 'Edit User: {username}',
    edit_venues: 'Edit Venue | Edit Venue | Edit Venues',
    email_address: 'Email Address',
    enabled: 'Enabled',
    end: 'End',
    end_date: 'End Date',
    end_time: 'End Time',
    entire_location: 'Entire Location',
    entry_hours_before_event: 'Entry Hours Before Event',
    event: 'Event',
    event_id: 'Event ID',
    events: 'Events',
    event_pricing: 'Event Pricing',
    exit_hours_after_event: 'Exit Hours After Event',
    external_id: 'External ID',
    fees: 'Fees',
    field: 'Field',
    filter_by: 'Filter By',
    filter_by_product_type: 'Filter by Product Type',
    filter_by_vertical: 'Filter by Vertical',
    filter_products_by_product_type: 'Filter Products by Product Type',
    filter_products_by_vertical: 'Filter Products by Vertical',
    first_available_start_time: 'First Available Start Time',
    first_name: 'First Name',
    forgot_password: 'Forgot password',
    four_day: '4-Day',
    four_day_abbr: '4D', // used as shorthand for "4-Day" in tight spaces
    frequency: 'Frequency',
    from: 'From',
    gated_barrier: 'Gated Barrier',
    general: 'General',
    gross_revenue: 'Gross Revenue',
    gross_revenue_by_channel: 'Gross Revenue by Channel',
    hide_non_live_venues: 'Hide non-live venues',
    hide_non_live_venues_n_hidden: 'Hide non-live venues ({n} hidden)',
    hour: 'Hour', // in the sense of "a specific time of day" - e.g., "4:00 pm"
    hours: 'Hours', // in the sense of "hours that a facility is open"
    hour_count_short: '{n} hr',
    id: 'ID',
    in_out: 'In & Out',
    include_summary_row: 'Include Summary Row',
    internal_notes: 'Internal Notes',
    inventory: 'Inventory',
    key_performance_indicators_abbr: 'KPIs',
    language: 'Language',
    last_n_days: 'Last {n} days',
    last_available_start_time: 'Last Available Start Time',
    last_name: 'Last Name',
    license_plate: 'License Plate',
    list: 'List',
    loading_events: 'Loading events…',
    location: 'Location',
    location_parcs: '{location_name}: PARCS',
    location_solutions: '{location_name}: Solutions',
    location_users: '{location_name}: Users',
    location_venues: '{location_name}: Venues',
    locations: 'Locations',
    locations_one_or_more: 'Location(s)',
    log: 'Log',
    log_id: 'Log ID',
    manage_spot_rates: 'Manage Spot Rates',
    max_on_lot: 'Max On Lot',
    median_duration: 'Median Duration',
    metric: 'Metric',
    month: 'Month',
    month_abbr: 'M', // used as shorthand for "Month" in tight spaces
    multi_day: 'Multi-Day',
    multi_day_discounts: 'Multi-Day Discounts',
    name: 'Name',
    net_revenue: 'Net Revenue',
    new: 'New',
    new_password: 'New Password',
    new_product: 'New Product',
    no_events_found: 'No events found',
    none: 'None',
    number_of_active_locations: '# of active locations:',
    number_of_transactions: '# of Transactions',
    occupancy_pct: 'Occupancy %',
    ocra_channel_facility_id: 'Ocra Channel Facility ID',
    ocra_confirm_your_account: 'Ocra – Confirm Your Account',
    ocra_forgot_password: 'Ocra – Forgot Password',
    ocra_generated_revenue: 'Ocra-Generated Revenue',
    ocra_id: 'Ocra ID',
    ocra_log_in: 'Ocra — Log In',
    ocra_reset_your_password: 'Ocra – Reset Your Password',
    old: 'Old',
    on_lot: 'On Lot',
    open: 'Open', // in the sense of "Opening Time"
    open_24_hours: 'Open 24 hours',
    online_allocation: 'Online Allocation',
    organization_name: 'Organization Name',
    organization: 'Organization',
    organizations: 'Organizations',
    overview: 'Overview',
    products_selected: 'Products ({selected}/{total})',
    page_not_found: 'Page Not Found',
    parcs: 'PARCS',
    parcs_inventory: 'PARCS Inventory',
    parcs_revenue: 'PARCS Revenue',
    password: 'Password',
    payout_date: 'Payout Date',
    peak_occupancy_times: 'Peak Occupancy Times',
    phone_number: 'Phone Number',
    preferences: 'Preferences',
    price: 'Price',
    product: 'Product',
    product_name: 'Product Name',
    product_type: 'Product Type',
    products: 'Products',
    promo_codes: 'Promo Codes',
    rate_id: 'Rate ID',
    rate_management: 'Rate Management',
    rate_types: 'Rate Types',
    reason: 'Reason',
    receive_daily_summary_emails: 'Receive daily summary emails',
    remove_tier: 'Remove Tier',
    repeat_new_password: 'Repeat New Password',
    repeat_parkers: 'Repeat Parkers',
    reporting: 'Reporting',
    reporting_activity: 'Reporting: Activity',
    reporting_breakdowns: 'Reporting: Breakdowns',
    reporting_customers: 'Reporting: Customers',
    reporting_daily_revenue: 'Reporting: Daily Revenue',
    reporting_overview: 'Reporting: Overview',
    reporting_transactions: 'Reporting: Transactions',
    reservation_inventory: 'Reservation Inventory',
    reservation_revenue: 'Reservation Revenue',
    reset_your_password: 'Reset your password',
    revenue: 'Revenue',
    revenue_breakdown: 'Revenue Breakdown',
    revenue_by_channel: 'Revenue by Channel',
    revenue_by_vertical: 'Revenue by Vertical',
    revenue_by_weekday: 'Revenue by Weekday',
    revpas: 'REVPAS',
    role: 'Role',
    set_all_to: 'Set All To',
    show_ids: 'Show IDs',
    sold: 'Sold',
    sold_allocated: '(sold/allocated)',
    solutions: 'Solutions',
    spot_override: 'Spot Override',
    spot_rates_abbr: 'Spot', // used as shorthand for "Spot Rates" in tight spaces
    spothero_discounts: 'SpotHero Discounts',
    start: 'Start',
    start_date: 'Start Date',
    start_date_short: 'Start Dt',
    start_time: 'Start Time',
    status: 'Status',
    summary: 'Summary',
    target_id: 'Target ID',
    target: 'Target',
    target_type: 'Target Type',
    temporary_password: 'Temporary Password',
    theme: 'Theme',
    tier_n: 'Tier {n}',
    tiered_pricing_structure_colon: 'Tiered Pricing Structure:',
    time: 'Time',
    time_zone: 'Time Zone',
    to: 'To',
    today: 'Today',
    total: 'Total',
    total_commission: 'Total Commission',
    total_gross_revenue: 'Total Gross Revenue',
    total_inventory: 'Total Inventory',
    total_net_revenue: 'Total Net Revenue',
    total_occupancy: 'Total Occupancy',
    total_parking_spaces: 'Total Parking Spaces',
    total_revenue: 'Total Revenue',
    total_transactions: 'Total Transactions',
    transaction_date: 'Transaction Date',
    transaction_date_short: 'Trans Dt',
    transaction_date_time: 'Transaction Date/Time',
    transaction_id: 'Transaction ID',
    transactions: 'Transactions',
    transactions_and_average_daily_rate_by_weekday: 'Transactions & ADR by Weekday',
    transactions_by_channel: 'Transactions by Channel',
    transactions_by_vertical: 'Transactions by Vertical',
    unchanged: 'Unchanged',
    universal_inventory: 'Universal Inventory',
    update_fee: 'Update Fee',
    updated: 'Updated',
    uri: 'Universal Rates & Inventory',
    user_created: 'User created',
    user_updated: 'User updated',
    username_or_email_address: 'Username or Email Address',
    username: 'Username',
    user_solutions_with_username: 'User Solutions: {username}',
    user: 'User',
    users: 'Users',
    users_with_access: 'Users with Access',
    velocity: 'Velocity',
    velocity_duration_in_hours: '{n} hour | {n} hours',
    velocity_window: 'Velocity Window',
    venue: 'Venue',
    venue_id: 'Venue ID',
    venue_name: 'Venue Name',
    venue_pricing: 'Venue Pricing',
    venues: 'Venues',
    verification_code: 'Verification Code',
    vertical: 'Vertical',
    verticals: 'Verticals',
    view: 'View',
    weekdays: 'Weekdays',
    weekends: 'Weekends',
    year: 'Year',
    year_to_date: 'Year to Date',
    year_to_date_abbr: 'YTD',
    your_profile: 'Your Profile',
  },

  /**
   * Validation errors.
   */
  validations: {
    decimal: 'Must be a decimal',
    dollar_amount: 'Must be a valid dollar amount',
    earlier_than_end_date: 'Must be earlier than the End Date',
    email: 'Must be a valid email address',
    fails_password_requirements: 'Doesn\'t meet the requirements',
    fails_username_requirements: 'Lowercase letters, digits, and underscores only',
    integer: 'Must be an integer',
    later_than_start_date: 'Must be later than the Start Date',
    on_half_hour: 'Must be on the hour or half-hour',
    minLength: 'Must be at least {min} characters long',
    maxLength: 'Can\'t be more than {max} characters long',
    minTiers: 'Must have at least {min} tiers',
    maxTiers: 'Can\'t have more than {max} tiers',
    minValue: 'Must be at least {min}',
    maxValue: 'Can\'t be more than {max}',
    must_be_after_open: 'Must be after open',
    must_be_before_close: 'Must be before close',
    not_more_than_one_hour_in_past: 'Must not be more than one hour in the past',
    no_more_than_60_days_after_start: 'Can\'t be more than 60 days after start date',
    not_in_past: 'Must not be in the past',
    passwords_match: 'Passwords must match',
    positive: 'Must be positive',
    required: 'Required',
    start_date_not_after_end_date: 'Can\'t be after end',
    too_high: 'Too high',
    end_date_not_before_start_date: 'Can\'t be before start',
    url: 'Must be a valid URL',
  },

}

export const datetimeFormats: DefaultDateTimeFormatSchema = {
  month_day_numeric: {
    month: 'numeric', day: 'numeric',
  },
  short: {
    year: 'numeric', month: 'short', day: 'numeric'
  },
  long: {
    year: 'numeric', month: 'short', day: 'numeric',
    weekday: 'short', hour: 'numeric', minute: 'numeric'
  },
  ymd: {
    year: 'numeric', month: 'numeric', day: 'numeric',
  },
  ymd_hm: {
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric',
  },
  ymd_hms: {
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric',
  },
}

export const numberFormats: DefaultNumberFormatSchema = {
  currency: {
    style: 'currency', currency: 'USD', notation: 'standard'
  },
  decimal: {
    style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2
  },
  percent: {
    style: 'percent', useGrouping: false
  },
}
